import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColDef, ICellRendererParams, SideBarDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Icon } from "@iconify/react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Dialog from "@mui/material/Dialog";
import { toast } from "react-toastify";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import { Action, Resource } from "../../utils/interfaces/Companies";
import { formatDisplayDate } from "../../utils/date";
import { formatConversionRatio } from "../../utils/string";
import {
  useExportToExcel,
  useTeamMemberGrants,
  useTeamMemberGrantDetails,
  useTeamMemberVestingDetails,
  useExportToExcelManager,
} from "../../queries";
import { useEvaluateMatrixQuery } from "../../queries/allocationMatrix";
import { useCompanyStore } from "../../store/companyStore";
import { getCurrencySymbol } from "../../utils/currencyFormatter";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { IconCTAButton, decodeAuthToken } from "../../components/utils";
import { useAuthStore } from "../../store";
import { GrantApprovalActionDialog } from "./GrantApprovalActionDialog";
import { ExportImport } from "../esopOverview/GrantsTable";
import { useTableStateManagement } from "../../components/shared/TableHook";
import { TableId } from "../../constants/TableIdConstants";
import { useDeleteTableState } from "../../queries/AgGridCacheQueries";
import CloudSetting from "../../components/shared/CloudIcon";
import Avatar from "../esopOverview/Avatar";
import SearchInput from "../../components/shared/SearchInput";
import AdvancedAgGridSearch from "../../components/AdvancedAgGridSearch";
import { getManagerViewEmployeeToken } from "../../api/managerPortal";
import {
  closeEmployeePreviewFrame,
  listenForEmployeePreviewRenewal,
} from "../../utils/employeePreviewFrame";
import { EmployeePortalPreview } from "../../components/EmployeePortalPreview";
import { AllocationMatrixInfoCard } from "../esopOverview/AllocationMatrixInfoCard";
import { RuleScope } from "../../store/useAllocationMatrixStore";
import { calculateAllocationValue } from "../../utils/allocationMatrixValue";
import "./managerDashboard.css";

interface TeamMember {
  id: string;
  name: string;
  hrId: string;
  status: string;
  dateOfJoining: string;
  department: string | null;
  designation: string | null;
  grantCount: number;
  totalVested: number;
  totalUnvested: number;
  totalGranted: number;
  totalNetVested: number;
  totalOutstanding: number;
  totalExercised: number;
  totalStockExercised: number;
  totalCashedOut: number;
  totalLapsed: number;
  totalForfeited: number;
  pendingCount: number;
  grants?: any[];
}

function ManagerGrantsView({ employeeId }: { employeeId: string }) {
  const { data: grants, isLoading, error } = useTeamMemberGrants(employeeId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <CircularProgress size={30} style={{ color: "#e85936" }} />
      </div>
    );
  }

  if (error || !grants) {
    return (
      <div className="text-red-500 py-6 text-center">
        Failed to load grants.
      </div>
    );
  }

  if (grants.length === 0) {
    return (
      <div className="text-gray-500 py-6 text-center font-medium">
        No grants found for this employee.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-100 rounded-lg">
      <Table className="min-w-full">
        <TableHead>
          <TableRow className="bg-gray-50/75">
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
            >
              Identifier
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
            >
              Plan
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
            >
              Date of Grant
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
              align="right"
            >
              Granted
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
              align="right"
            >
              Vested
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
              align="right"
            >
              Outstanding
            </TableCell>
            <TableCell
              style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
            >
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {grants.map((g: any) => (
            <TableRow key={g.id} hover>
              <TableCell
                style={{ fontSize: 13, color: "#1f2937", fontWeight: 500 }}
              >
                {g.identifier}
              </TableCell>
              <TableCell style={{ fontSize: 13, color: "#4b5563" }}>
                {g.planName ?? "-"}
              </TableCell>
              <TableCell style={{ fontSize: 13, color: "#4b5563" }}>
                {g.dateOfGrant ? formatDisplayDate(g.dateOfGrant) : "-"}
              </TableCell>
              <TableCell
                style={{ fontSize: 13, color: "#1f2937" }}
                align="right"
              >
                {g.grantedOptions?.toLocaleString()}
              </TableCell>
              <TableCell
                style={{ fontSize: 13, color: "#1f2937" }}
                align="right"
              >
                {g.vestedOptions?.toLocaleString()}
              </TableCell>
              <TableCell
                style={{ fontSize: 13, color: "#1f2937" }}
                align="right"
              >
                {g.outstanding?.toLocaleString()}
              </TableCell>
              <TableCell style={{ fontSize: 13 }}>
                <GrantStateBadge state={g.state} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ManagerGrantDetailsView({ grantId }: { grantId: string }) {
  const { data: grant, isLoading, error } = useTeamMemberGrantDetails(grantId);
  const savedMatchedRule =
    grant?.matchedRule ?? grant?.additionalMetaData?.matchedRule ?? null;
  const { data: evaluatedAllocation } = useEvaluateMatrixQuery(
    grant?.allocationMatrixId,
    grant?.employeeId,
    grant?.id
  );
  const currencySymbol = getCurrencySymbol();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <CircularProgress size={30} style={{ color: "#e85936" }} />
      </div>
    );
  }

  if (error || !grant) {
    return (
      <div className="text-red-500 py-6 text-center">
        Failed to load grant details.
      </div>
    );
  }

  const allocationRule =
    savedMatchedRule ??
    evaluatedAllocation?.matchedRule ??
    evaluatedAllocation?.rule ??
    null;
  const currentSharePrice =
    grant.currentSharePrice ?? grant.pricePerOption ?? 0;
  const priorGrants = (grant.employeeGrants ?? []).filter(
    (employeeGrant: any) =>
      employeeGrant.id !== grant.id &&
      employeeGrant.grantIdentifier !== grant.identifier
  );
  const allocatedValue = allocationRule
    ? priorGrants.reduce(
        (total: number, employeeGrant: any) =>
          total +
          calculateAllocationValue({
            options: employeeGrant.optionsGranted ?? 0,
            ratio: employeeGrant.ratio,
            fmv: currentSharePrice,
            strikePrice: employeeGrant.grantPrice ?? 0,
            allocationMode: allocationRule.allocationMode,
            costOrGain: allocationRule.costOrGain,
          }),
        0
      )
    : 0;
  const grantValue = allocationRule
    ? calculateAllocationValue({
        options: grant.grantedOptions ?? 0,
        ratio: grant.ratio,
        fmv: currentSharePrice,
        strikePrice: grant.strikePrice ?? 0,
        allocationMode: allocationRule.allocationMode,
        costOrGain: allocationRule.costOrGain,
      })
    : 0;
  const allocationEvaluation = allocationRule
    ? {
        matched: true,
        rule: allocationRule,
        matchedRule: allocationRule,
        matchedOn: evaluatedAllocation?.matchedOn,
      }
    : evaluatedAllocation ?? null;
  const remainingMinimum =
    allocationRule?.quantumMin != null
      ? Math.max(allocationRule.quantumMin - allocatedValue, 0)
      : null;
  const remainingMaximum =
    allocationRule?.quantumMax != null
      ? Math.max(allocationRule.quantumMax - allocatedValue, 0)
      : null;
  const isAboveLimit =
    allocationRule?.scope === RuleScope.PER_EMPLOYEE &&
    remainingMaximum != null &&
    grantValue > remainingMaximum;
  const isBelowLimit =
    allocationRule?.scope === RuleScope.PER_EMPLOYEE &&
    remainingMinimum != null &&
    grantValue < remainingMinimum;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
          <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
            Grant Information
          </h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Identifier:</span>{" "}
              <span className="font-semibold text-gray-800">
                {grant.identifier}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">State:</span>{" "}
              <span>
                <GrantStateBadge state={grant.state} />
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Grant Type:</span>{" "}
              <span className="font-medium text-gray-800">
                {grant.grantType}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date of Grant:</span>{" "}
              <span className="font-medium text-gray-800">
                {grant.dateOfGrant ? formatDisplayDate(grant.dateOfGrant) : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Vesting Start Date:</span>{" "}
              <span className="font-medium text-gray-800">
                {grant.vestingStartDate
                  ? formatDisplayDate(grant.vestingStartDate)
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
          <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
            Option & Financials
          </h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Granted Options:</span>{" "}
              <span className="font-semibold text-gray-800">
                {grant.grantedOptions?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Strike Price:</span>{" "}
              <span className="font-semibold text-gray-800">
                {grant.strikePrice
                  ? `${currencySymbol}${grant.strikePrice.toLocaleString()} ${
                      grant.strikePriceType?.replace(/_/g, " ") ?? ""
                    }`
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Grant PPS:</span>{" "}
              <span className="font-semibold text-gray-800">
                {grant.pricePerOption != null
                  ? `${currencySymbol}${grant.pricePerOption.toLocaleString()}`
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Plan Name:</span>{" "}
              <span className="font-medium text-gray-800">
                {grant.plan?.name ?? "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Conversion Ratio:</span>{" "}
              <span className="font-medium text-gray-800">
                {grant.ratio ? formatConversionRatio(grant.ratio) : "1:1"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {grant.allocationMatrixId && allocationRule && (
        <AllocationMatrixInfoCard
          rule={allocationRule}
          grantValue={grantValue}
          isAboveLimit={isAboveLimit}
          isBelowLimit={isBelowLimit}
          employeeId={grant.employeeId}
          evalData={allocationEvaluation}
          sharePrice={grant.pricePerOption ?? 0}
          currentSharePrice={currentSharePrice}
          grantPrice={grant.strikePrice ?? 0}
          currentGrantId={grant.id}
          employeeGrants={grant.employeeGrants}
          managerScoped
        />
      )}

      {grant.repricingHistory && grant.repricingHistory.length > 0 && (
        <div className="border border-gray-100 rounded-lg p-4">
          <h4 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
            Repricing History
          </h4>
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <Table className="min-w-full">
              <TableHead>
                <TableRow className="bg-gray-50/75">
                  <TableCell
                    style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                  >
                    Effective Date
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                    align="right"
                  >
                    Options Repriced
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                    align="right"
                  >
                    Prev Strike Price
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                    align="right"
                  >
                    Repriced Strike Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grant.repricingHistory.map((rep: any) => (
                  <TableRow key={rep.id} hover>
                    <TableCell style={{ fontSize: 13, color: "#4b5563" }}>
                      {rep.effectiveDate
                        ? formatDisplayDate(rep.effectiveDate)
                        : "-"}
                    </TableCell>
                    <TableCell
                      style={{ fontSize: 13, color: "#1f2937" }}
                      align="right"
                    >
                      {rep.optionsRepriced?.toLocaleString()}
                    </TableCell>
                    <TableCell
                      style={{ fontSize: 13, color: "#1f2937" }}
                      align="right"
                    >
                      {rep.previousStrikePrice?.toLocaleString()}
                    </TableCell>
                    <TableCell
                      style={{ fontSize: 13, color: "#1f2937" }}
                      align="right"
                    >
                      {rep.repricedStrikePrice?.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export function ManagerVestingView({
  employeeId,
  grantId,
  grantIdentifier,
}: {
  employeeId: string;
  grantId?: string;
  grantIdentifier?: string;
}) {
  const {
    data: vestingData,
    isLoading,
    error,
  } = useTeamMemberVestingDetails(employeeId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <CircularProgress size={30} style={{ color: "#e85936" }} />
      </div>
    );
  }

  if (error || !vestingData) {
    return (
      <div className="text-red-500 py-6 text-center">
        Failed to load vesting schedule.
      </div>
    );
  }

  const filteredData = vestingData.filter((v: any) => {
    if (grantId && v.optionHolderId !== grantId) return false;
    return v.vestedOptions > 0;
  });

  if (filteredData.length === 0) {
    return (
      <div className="text-gray-500 py-6 text-center font-medium">
        No vesting events found.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {grantIdentifier && (
        <div className="text-sm font-medium text-gray-700 bg-orange-50/50 border border-orange-100 rounded px-3 py-2 inline-block">
          Grant Identifier:{" "}
          <span className="text-[#e85936] font-semibold">
            {grantIdentifier}
          </span>
        </div>
      )}
      <div className="overflow-x-auto border border-gray-100 rounded-lg">
        <Table className="min-w-full">
          <TableHead>
            <TableRow className="bg-gray-50/75">
              <TableCell
                style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
              >
                Date
              </TableCell>
              {!grantId && (
                <TableCell
                  style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                >
                  Grant
                </TableCell>
              )}
              <TableCell
                style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                align="right"
              >
                Vested Options
              </TableCell>
              <TableCell
                style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
                align="right"
              >
                Cumulative Vested
              </TableCell>
              <TableCell
                style={{ fontWeight: 600, fontSize: 12, color: "#4b5563" }}
              >
                Type / Milestone
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((v: any, index: number) => (
              <TableRow key={index} hover>
                <TableCell style={{ fontSize: 13, color: "#4b5563" }}>
                  {v.date ? formatDisplayDate(v.date) : "-"}
                </TableCell>
                {!grantId && (
                  <TableCell
                    style={{ fontSize: 13, color: "#1f2937", fontWeight: 500 }}
                  >
                    {v.identifier ?? "-"}
                  </TableCell>
                )}
                <TableCell
                  style={{ fontSize: 13, color: "#1f2937" }}
                  align="right"
                >
                  {v.vestedOptions?.toLocaleString()}
                </TableCell>
                <TableCell
                  style={{ fontSize: 13, color: "#1f2937" }}
                  align="right"
                >
                  {v.accumulatedVestedOptionsForEmployee?.toLocaleString()}
                </TableCell>
                <TableCell style={{ fontSize: 13, color: "#4b5563" }}>
                  {v.vestingType === "Milestone"
                    ? `Milestone: ${
                        v.milestoneClassification ||
                        v.milestoneDescription ||
                        "Milestone reached"
                      }`
                    : "Time Based"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatusCellRenderer({ value }: ICellRendererParams) {
  const s = value?.toUpperCase();
  const dotColor =
    s === "EMPLOYED" || s === "ACTIVE"
      ? "bg-green-500"
      : s === "RESIGNED"
      ? "bg-red-400"
      : "bg-gray-400";
  const textColor =
    s === "EMPLOYED" || s === "ACTIVE"
      ? "text-green-700"
      : s === "RESIGNED"
      ? "text-red-600"
      : "text-gray-500";
  const label =
    s === "EMPLOYED" || s === "ACTIVE"
      ? "Employed"
      : s === "RESIGNED"
      ? "Resigned"
      : value ?? "-";
  return (
    <div className="flex items-center h-full">
      <span
        className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${textColor}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
        />
        {label}
      </span>
    </div>
  );
}

function CtaCellRenderer(params: ICellRendererParams) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const onOpenPortal: (employeeId: string) => void =
    (params as any).onOpenPortal ?? (() => {});
  const onViewGrants: (employeeId: string, employeeName: string) => void =
    (params as any).onViewGrants ?? (() => {});
  const employeeId: string = params.node?.allLeafChildren?.[0]?.data?.id ?? "";
  const employeeName: string =
    params.node?.allLeafChildren?.[0]?.data?.name ?? params.node?.key ?? "";
  return (
    <div className="flex w-full items-center justify-end h-full pr-1">
      <button
        aria-label="More actions"
        className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <ListSubheader
          disableSticky
          sx={{ fontSize: 13, lineHeight: "32px", color: "text.disabled" }}
        >
          More actions
        </ListSubheader>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onOpenPortal(employeeId);
          }}
          sx={{ fontSize: 13, minWidth: 140 }}
        >
          Preview employee
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onViewGrants(employeeId, employeeName);
          }}
          sx={{ fontSize: 13, minWidth: 140 }}
        >
          View grants
        </MenuItem>
      </Menu>
    </div>
  );
}

const GRANT_STATE_COLORS: Record<string, { dot: string; text: string }> = {
  GRANTED: { dot: "bg-green-500", text: "text-green-700" },
  OFFERED: { dot: "bg-amber-400", text: "text-amber-700" },
  FORFEITED: { dot: "bg-red-400", text: "text-red-600" },
  REJECTED: { dot: "bg-red-400", text: "text-red-600" },
  LAPSED: { dot: "bg-gray-400", text: "text-gray-500" },
};

export function GrantStateBadge({ state }: { state: string }) {
  const s = state?.toUpperCase();
  const colors = GRANT_STATE_COLORS[s] ?? {
    dot: "bg-blue-400",
    text: "text-blue-600",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${colors.text}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.dot}`}
      />
      {s?.replace(/_/g, " ") ?? "-"}
    </span>
  );
}

export function GrantMenuRenderer(p: ICellRendererParams) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isForApproval = p.data?.state === "FOR_APPROVAL";
  const isPendingApprover = !!p.data?.isPendingApprover;
  const onViewGrant: (employeeId: string, grantId: string) => void =
    (p as any).onViewGrant ?? (() => {});
  const onViewVestingSchedule: (
    employeeId: string,
    grantId: string,
    grantIdentifier: string
  ) => void = (p as any).onViewVestingSchedule ?? (() => {});
  const onApprovalAction: (
    employeeId: string,
    grantId: string,
    isPendingApprover: boolean
  ) => void = (p as any).onApprovalAction ?? (() => {});

  const employeeId: string = p.data?.employeeId ?? p.data?.id ?? "";
  const grantId: string = p.data?.grantId ?? p.data?.id ?? "";
  const grantIdentifier: string = p.data?.identifier ?? "";
  return (
    <div className="flex items-center justify-end h-full pr-1">
      <button
        className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <ListSubheader
          disableSticky
          sx={{ fontSize: 13, lineHeight: "32px", color: "text.disabled" }}
        >
          More actions
        </ListSubheader>
        {isForApproval && (
          <MenuItem
            disabled={!isPendingApprover}
            onClick={() => {
              setAnchorEl(null);
              onApprovalAction(employeeId, grantId, isPendingApprover);
            }}
            sx={{ fontSize: 13, minWidth: 140 }}
          >
            Review Grant
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onViewGrant(employeeId, grantId);
          }}
          sx={{ fontSize: 13, minWidth: 140 }}
        >
          View grant
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onViewVestingSchedule(employeeId, grantId, grantIdentifier);
          }}
          sx={{ fontSize: 13, minWidth: 140 }}
        >
          Vesting schedule
        </MenuItem>
      </Menu>
    </div>
  );
}

type GroupByValue = "employee" | "department" | "status";

const numberFilterParams = {
  buttons: ["reset"],
  filterOptions: [
    "equals",
    "notEqual",
    "lessThan",
    "lessThanOrEqual",
    "greaterThan",
    "greaterThanOrEqual",
    "inRange",
    {
      displayKey: "lessThanWithNulls",
      displayName: "Less Than with Nulls",
      predicate: ([filterValue]: any, cellValue: number | null) =>
        cellValue == null || cellValue < filterValue,
    },
    {
      displayKey: "greaterThanWithNulls",
      displayName: "Greater Than with Nulls",
      predicate: ([filterValue]: any, cellValue: number | null) =>
        cellValue == null || cellValue > filterValue,
    },
    {
      displayKey: "betweenExclusive",
      displayName: "Between (Exclusive)",
      predicate: ([fv1, fv2]: any, cellValue: number | null) =>
        cellValue == null || (fv1 < cellValue && fv2 > cellValue),
      numberOfInputs: 2,
    },
  ],
};

export function TeamMembersGrid({
  members,
  employeeIdToOpen,
  onEmployeeGrantsOpened,
}: {
  members: TeamMember[];
  employeeIdToOpen?: string | null;
  onEmployeeGrantsOpened?: () => void;
}) {
  const { permission } = usePermissionStore();
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setIsGridReady] = useState(false);
  const [search, setSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const { mutate: exportExcel } = useExportToExcelManager();
  const { companyData } = useCompanyStore();
  const [employeeDetailsDialog, setEmployeeDetailsDialog] = useState<{
    open: boolean;
    previewAccessToken: string;
    employeeId: string;
  }>({ open: false, previewAccessToken: "", employeeId: "" });
  const employeePreviewFrame = useRef<HTMLIFrameElement>(null);
  const closingEmployeePreview = useRef(false);
  useEffect(() => {
    if (!employeeDetailsDialog.open || !employeeDetailsDialog.employeeId)
      return undefined;
    return listenForEmployeePreviewRenewal(
      () => employeePreviewFrame.current,
      employeeDetailsDialog.employeeId,
      async (employeeId) =>
        (await getManagerViewEmployeeToken(employeeId)).data,
      () => closingEmployeePreview.current
    );
  }, [employeeDetailsDialog.open, employeeDetailsDialog.employeeId]);

  const { accessToken } = useAuthStore();
  const currentUserId = accessToken
    ? decodeAuthToken(accessToken).id?.toString()
    : undefined;

  const [managerDetailDialog, setManagerDetailDialog] = useState<{
    open: boolean;
    type:
      | "grants"
      | "grant-details"
      | "vesting-schedule"
      | "approval-action"
      | null;
    employeeId?: string;
    employeeName?: string;
    grantId?: string;
    grantIdentifier?: string;
    isPendingApprover?: boolean;
  }>({ open: false, type: null });

  useEffect(() => {
    if (!employeeIdToOpen) return;
    if (!gridRef.current?.api) return;

    let didExpand = false;
    gridRef.current?.api?.forEachNode((node) => {
      const isEmployeeGroup =
        node.group &&
        node.allLeafChildren?.some(
          (child: any) =>
            child.data?.id === employeeIdToOpen && child.data?.grantId != null
        );
      if (isEmployeeGroup) {
        node.setExpanded(true);
        didExpand = true;
      }
    });
    if (didExpand) onEmployeeGrantsOpened?.();
  }, [employeeIdToOpen, members, isGridReady, onEmployeeGrantsOpened]);

  function redirectToEmployeePortal(employeeId: string) {
    if (!employeeId) return;
    getManagerViewEmployeeToken(employeeId).then(
      (data) => {
        const jwtAccessToken = data.data;
        setEmployeeDetailsDialog({
          open: true,
          previewAccessToken: jwtAccessToken,
          employeeId,
        });
      },
      (err: any) => {
        toast(err?.response?.data?.reason ?? "Failed to open employee portal", {
          type: "error",
          autoClose: 2000,
        });
      }
    );
  }
  function closeEmployeePreview() {
    if (closingEmployeePreview.current) return;
    closingEmployeePreview.current = true;
    closeEmployeePreviewFrame(employeePreviewFrame.current, () => {
      closingEmployeePreview.current = false;
      setEmployeeDetailsDialog({
        open: false,
        previewAccessToken: "",
        employeeId: "",
      });
    });
  }

  const canViewGrants = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Grant,
    Action.Read
  );

  const canDownloadReports = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Document,
    Action.Read
  );

  useEffect(() => {
    gridRef.current?.api?.destroyFilter("ag-Grid-AutoColumn");
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: true,
      filterParams: { buttons: ["reset"] },
      menuTabs: ["filterMenuTab"],
    }),
    []
  );

  const gridRowData = useMemo(() => {
    const rows: any[] = [];
    for (const emp of members) {
      if (!canViewGrants || !emp.grants || emp.grants.length === 0) {
        rows.push({
          id: emp.id,
          name: emp.name,
          hrId: emp.hrId,
          status: emp.status,
          dateOfJoining: emp.dateOfJoining,
          department: emp.department,
          designation: emp.designation,
          totalGranted: emp.totalGranted,
          totalVested: emp.totalVested,
          totalUnvested: emp.totalUnvested,
          totalNetVested: emp.totalNetVested,
          totalOutstanding: emp.totalOutstanding,
          totalExercised: emp.totalExercised,
          totalStockExercised: emp.totalStockExercised,
          totalCashedOut: emp.totalCashedOut,
          totalLapsed: emp.totalLapsed,
          totalForfeited: emp.totalForfeited,
          // grant specific fields
          grantId: null,
          identifier: null,
          planName: null,
          grantedOptions: null,
          vestedOptions: null,
          unvestedOptions: null,
          netVested: null,
          outstanding: null,
          exercised: null,
          stockExercised: null,
          cashedOut: null,
          lapsed: null,
          forfeited: null,
          state: null,
          dateOfGrant: null,
        });
      } else {
        for (const grant of emp.grants) {
          rows.push({
            id: emp.id,
            name: emp.name,
            hrId: emp.hrId,
            status: emp.status,
            dateOfJoining: emp.dateOfJoining,
            department: emp.department,
            designation: emp.designation,
            totalGranted: emp.totalGranted,
            totalVested: emp.totalVested,
            totalUnvested: emp.totalUnvested,
            totalNetVested: emp.totalNetVested,
            totalOutstanding: emp.totalOutstanding,
            totalExercised: emp.totalExercised,
            totalStockExercised: emp.totalStockExercised,
            totalCashedOut: emp.totalCashedOut,
            totalLapsed: emp.totalLapsed,
            totalForfeited: emp.totalForfeited,
            // grant specific fields
            grantId: grant.id,
            identifier: grant.identifier,
            isPendingApprover:
              grant.state === "FOR_APPROVAL" && !!grant.isPendingApprover,
            planName: grant.planName,
            grantedOptions: grant.grantedOptions,
            vestedOptions: grant.vestedOptions,
            unvestedOptions: grant.unvestedOptions,
            netVested: grant.netVested,
            outstanding: grant.outstanding,
            exercised: grant.exercised,
            stockExercised: grant.stockExercised,
            cashedOut: grant.cashedOut,
            lapsed: grant.lapsed,
            forfeited: grant.forfeited,
            state: grant.state,
            dateOfGrant: grant.dateOfGrant,
          });
        }
      }
    }
    return rows;
  }, [members, canViewGrants]);

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      minWidth: 240,
      headerName: "Employee / Identifier",
      filter: "agSetColumnFilter",
      suppressMenu: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: any) => {
          if (params.node.group) {
            const data = params.node.allLeafChildren?.[0]?.data;
            if (!data) return <span>{params.value}</span>;

            if (params.node.field === "name") {
              return (
                <div className="flex items-center gap-3 h-full py-1">
                  <Avatar name={data.name} status={data.status} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-dark leading-tight">
                      {data.name}
                    </div>
                    <div className="text-[11px] text-gray-light leading-tight">
                      {[data.hrId, data.designation]
                        .filter((v) => v && v !== "")
                        .join(" | ")}
                    </div>
                  </div>
                </div>
              );
            }

            if (params.node.field === "department") {
              return (
                <span className="text-sm font-semibold text-gray-700">
                  {params.value || "No Department"}
                </span>
              );
            }

            if (params.node.field === "status") {
              const s = String(params.value).toUpperCase();
              const dotColor =
                s === "EMPLOYED" || s === "ACTIVE"
                  ? "bg-green-500"
                  : "bg-gray-400";
              const textColor =
                s === "EMPLOYED" || s === "ACTIVE"
                  ? "text-green-700"
                  : "text-gray-500";
              const label =
                s === "EMPLOYED" || s === "ACTIVE"
                  ? "Employed"
                  : s === "RESIGNED"
                  ? "Resigned"
                  : params.value ?? "-";
              return (
                <span
                  className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${textColor}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
                  />
                  {label}
                </span>
              );
            }

            return <span>{params.value}</span>;
          }

          // Leaf rows (Grants)
          const data = params.data;
          if (!data || !data.grantId)
            return <span className="text-gray-300">—</span>;

          return (
            <div className="flex flex-col justify-center h-full gap-0.5 py-1 pl-6">
              <span className="text-[12px] font-semibold font-mono text-gray-800 leading-tight">
                {data.identifier || "-"}
              </span>
              <span className="text-[10px] text-gray-400 leading-tight">
                {data.planName || "-"}
              </span>
            </div>
          );
        },
      },
    }),
    []
  );

  const gridApi = useRef<any>(null);
  const [advancedFilterApi, setAdvancedFilterApi] = useState<any>(null);

  const {
    filterCondition,
    setFilterCondition,
    isSaving: isStateSaving,
    uploadFilter,
    uploadColumn,
    tableState,
  } = useTableStateManagement(gridApi, TableId.managerDashboardTable);

  const onGridReady = (params: any) => {
    gridApi.current = params;
    setAdvancedFilterApi(params.api);
    setIsGridReady(true);
  };

  const columnDefs = useMemo<ColDef[]>(
    () =>
      [
        {
          headerName: "Employee / Identifier",
          field: "name",
          rowGroup: true,
          hide: true,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
        },
        {
          headerName: "Granted",
          field: "grantedOptions",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalGranted;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) =>
                  sum + (child.data?.grantedOptions || 0),
                0
              );
            }
            return params.data?.grantedOptions;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Vested",
          field: "vestedOptions",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalVested;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) =>
                  sum + (child.data?.vestedOptions || 0),
                0
              );
            }
            return params.data?.vestedOptions;
          },
          cellRenderer: (p: ICellRendererParams) => {
            if (p.value == null)
              return <span className="text-gray-300">—</span>;
            return (
              <span className="text-[12px] font-medium text-blue-600">
                {p.value.toLocaleString()}
              </span>
            );
          },
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Unvested",
          field: "unvestedOptions",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalUnvested;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) =>
                  sum + (child.data?.unvestedOptions || 0),
                0
              );
            }
            return params.data?.unvestedOptions;
          },
          cellRenderer: (p: ICellRendererParams) => {
            if (p.value == null)
              return <span className="text-gray-300">—</span>;
            return (
              <span className="text-[12px] font-medium text-amber-600">
                {p.value.toLocaleString()}
              </span>
            );
          },
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Net Vested",
          field: "netVested",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalNetVested;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) => sum + (child.data?.netVested || 0),
                0
              );
            }
            return params.data?.netVested;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Outstanding",
          field: "outstanding",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalOutstanding;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) =>
                  sum + (child.data?.outstanding || 0),
                0
              );
            }
            return params.data?.outstanding;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Exercised",
          field: "exercised",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalExercised;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) => sum + (child.data?.exercised || 0),
                0
              );
            }
            return params.data?.exercised;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Cashed Out",
          field: "cashedOut",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalCashedOut;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) => sum + (child.data?.cashedOut || 0),
                0
              );
            }
            return params.data?.cashedOut;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Lapsed",
          field: "lapsed",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalLapsed;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) => sum + (child.data?.lapsed || 0),
                0
              );
            }
            return params.data?.lapsed;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Forfeited",
          field: "forfeited",
          flex: 1,
          minWidth: 100,
          cellClass: "ag-right-aligned-cell",
          cellStyle: { textAlign: "right" },
          headerClass: "ag-right-aligned-header",
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.totalForfeited;
              }
              return params.node.allLeafChildren?.reduce(
                (sum: number, child: any) => sum + (child.data?.forfeited || 0),
                0
              );
            }
            return params.data?.forfeited;
          },
          valueFormatter: (p: any) =>
            p.value != null ? p.value.toLocaleString() : "-",
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Status",
          field: "status",
          flex: 1,
          minWidth: 120,
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.status;
              }
              return "";
            }
            return params.data?.state;
          },
          cellRenderer: (p: ICellRendererParams) => {
            if (p.node?.group) {
              if (p.node.field === "name") {
                return <StatusCellRenderer {...p} value={p.value} />;
              }
              return null;
            }
            return p.value ? <GrantStateBadge state={p.value} /> : null;
          },
          valueFormatter: (p: any) =>
            p.value ? p.value.replace(/_/g, " ") : p.value,
          filter: "agSetColumnFilter",
          filterParams: {
            buttons: ["reset"],
            valueFormatter: (p: any) =>
              p.value ? p.value.replace(/_/g, " ") : p.value,
          },
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "Date of Joining",
          field: "dateOfJoining",
          flex: 1,
          minWidth: 140,
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.field === "name") {
                return params.node.allLeafChildren?.[0]?.data?.dateOfJoining;
              }
              return "";
            }
            return params.data?.dateOfGrant;
          },
          valueFormatter: (p) => (p.value ? formatDisplayDate(p.value) : "-"),
          filter: "agDateColumnFilter",
          menuTabs: ["filterMenuTab"],
        },
        {
          headerName: "",
          field: "id",
          pinned: "right",
          width: 56,
          sortable: false,
          resizable: false,
          suppressColumnsToolPanel: true,
          cellRendererParams: {
            onOpenPortal: redirectToEmployeePortal,
            onViewGrants: (employeeId: string, employeeName: string) => {
              setManagerDetailDialog({
                open: true,
                type: "grants",
                employeeId,
                employeeName,
              });
            },
            onViewGrant: (employeeId: string, grantId: string) => {
              setManagerDetailDialog({
                open: true,
                type: "grant-details",
                employeeId,
                grantId,
              });
            },
            onViewVestingSchedule: (
              employeeId: string,
              grantId: string,
              grantIdentifier: string
            ) => {
              setManagerDetailDialog({
                open: true,
                type: "vesting-schedule",
                employeeId,
                grantId,
                grantIdentifier,
              });
            },
            onApprovalAction: (
              employeeId: string,
              grantId: string,
              isPendingApprover: boolean
            ) => {
              setManagerDetailDialog({
                open: true,
                type: "approval-action",
                employeeId,
                grantId,
                isPendingApprover,
              });
            },
          },
          cellRenderer: (p: ICellRendererParams) => {
            if (p.node?.group) {
              if (p.node.field === "name") {
                return <CtaCellRenderer {...p} />;
              }
              return null;
            }
            return <GrantMenuRenderer {...p} />;
          },
          cellStyle: { padding: 0 },
          suppressMenu: true,
        },
      ] as ColDef[],
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    gridApi.current?.api?.setQuickFilter(val);
  };

  const handleExport = () => {
    setIsExporting(true);
    const excelData = members.map((m) => ({
      name: m.name,
      hrId: m.hrId || "",
      status: m.status,
      dateOfJoining: m.dateOfJoining ? formatDisplayDate(m.dateOfJoining) : "",
      department: m.department || "",
      designation: m.designation || "",
      grantCount: m.grantCount,
      totalGranted: m.totalGranted,
      totalVested: m.totalVested,
      totalUnvested: m.totalUnvested,
      totalNetVested: m.totalNetVested,
      totalOutstanding: m.totalOutstanding,
      totalExercised: m.totalExercised,
      totalCashedOut: m.totalCashedOut,
      totalLapsed: m.totalLapsed,
      totalForfeited: m.totalForfeited,
    }));
    const headers = [
      { key: "name", value: "Employee Name" },
      { key: "hrId", value: "Employee ID" },
      { key: "status", value: "Status" },
      { key: "dateOfJoining", value: "Date of Joining" },
      { key: "department", value: "Department" },
      { key: "designation", value: "Designation" },
      { key: "grantCount", value: "Grant Count" },
      { key: "totalGranted", value: "Total Granted" },
      { key: "totalVested", value: "Total Vested" },
      { key: "totalUnvested", value: "Total Unvested" },
      { key: "totalNetVested", value: "Net Vested" },
      { key: "totalOutstanding", value: "Outstanding" },
      { key: "totalExercised", value: "Exercised" },
      { key: "totalCashedOut", value: "Cashed Out" },
      { key: "totalLapsed", value: "Lapsed" },
      { key: "totalForfeited", value: "Forfeited" },
    ];
    exportExcel(
      {
        heading: "Team Members",
        companyName: companyData?.name ?? "",
        data: excelData,
        headers,
        maskPiiData: false,
      },
      {
        onSuccess: async (res: any) => {
          const reportDto = res.data;
          const blob = await convertBase64ToBlob(
            reportDto.base64File ?? "",
            reportDto.fileType ??
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          );
          downloadBlobObject(blob, "Team_Members");
          setIsExporting(false);
        },
        onError: () => setIsExporting(false),
      }
    );
  };

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const openToolPanel = (key: string) => {
    if (gridApi.current?.api) {
      if (key === "columns") {
        if (!isColumnOpen) {
          gridApi.current.api.openToolPanel(key);
        } else {
          gridApi.current.api.closeToolPanel();
        }
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      } else {
        if (!isFilterOpen) {
          gridApi.current.api.openToolPanel(key);
        } else {
          gridApi.current.api.closeToolPanel();
        }
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const exportImportActions = useMemo(
    () => [{ name: "Export Excel", disabled: !canDownloadReports }],
    [canDownloadReports]
  );

  const handleExportImport = (action: any) => {
    if (action.name === "Export Excel") {
      handleExport();
    } else if (action.name === "Vesting Summary") {
      window.open("/v1/manager-portal/reports?type=vesting", "_blank");
    } else if (action.name === "Grant Summary") {
      window.open("/v1/manager-portal/reports?type=grants", "_blank");
    }
  };

  const rowClassRules = useMemo(
    () => ({
      "no-grants-group": (params: any) =>
        params.node?.group
          ? !params.node.allLeafChildren?.some(
              (child: any) => child.data?.grantId != null
            )
          : false,
    }),
    []
  );

  const onRowGroupOpened = useCallback((params: any) => {
    if (
      params.node?.expanded &&
      !params.node.allLeafChildren?.some(
        (child: any) => child.data?.grantId != null
      )
    ) {
      params.node.setExpanded(false);
    }
  }, []);

  const onRowClicked = useCallback((params: any) => {
    if (!params.node?.group) return;
    const target = params.event?.target as HTMLElement | undefined;
    if (target?.closest('[col-id="id"]')) return;
    params.node.setExpanded(!params.node.expanded);
  }, []);

  // Match the fixed viewport used by the admin grants table. Rows scroll inside
  // the grid rather than making the manager dashboard grow with the dataset.
  const gridHeight = "600px";

  return (
    <>
      <div className="manager-table-card flex w-full flex-col gap-4">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">
              Team Members
            </h2>
            <p className="mt-0.5 text-sm text-slate-400">
              {members.length} Employee{members.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExportImport
              actions={exportImportActions}
              onAction={handleExportImport}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <SearchInput
            className="!w-[300px]"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search"
          />
          <div className="flex items-center gap-2">
            <CloudSetting
              tableId={TableId.managerDashboardTable}
              isLoading={isStateSaving}
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              gridApi={gridApi}
            />
            <IconCTAButton
              value={"Columns"}
              onClick={() => openToolPanel("columns")}
              iconName={"fluent:column-triple-edit-20-regular"}
              className={`manager-table-tool-cta px-4 font-medium items-center flex flex-row ${
                isColumnOpen ? "manager-table-tool-cta-active" : ""
              }`}
              selected={isColumnOpen}
            />
            <IconCTAButton
              value={"Filters"}
              onClick={() => openToolPanel("filters")}
              iconName={"material-symbols:filter-alt"}
              className={`manager-table-tool-cta px-4 font-medium items-center flex flex-row ${
                isFilterOpen ? "manager-table-tool-cta-active" : ""
              }`}
              selected={isFilterOpen}
            />
            <AdvancedAgGridSearch
              gridApi={advancedFilterApi}
              tableIdentifier="managerDashboardTable"
            />
          </div>
        </div>

        {/* Grid */}
        <div
          className="w-full h-full max-h-full overflow-x-auto manager-dashboard-grid ag-theme-material"
          style={{ height: gridHeight }}
        >
          <AgGridReact
            key="employee"
            ref={gridRef}
            rowData={gridRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            groupDisplayType="singleColumn"
            groupDefaultExpanded={0}
            rowClassRules={rowClassRules}
            onRowGroupOpened={onRowGroupOpened}
            onRowClicked={onRowClicked}
            suppressAggFuncInHeader
            animateRows
            pagination
            paginationPageSize={15}
            rowHeight={56}
            headerHeight={40}
            suppressCellFocus
            suppressMenuHide
            sideBar={sideBar}
            onFilterChanged={(grid) => {
              const filterModel = grid.api.getFilterModel();
              uploadFilter(filterModel);
            }}
            onColumnEverythingChanged={uploadColumn}
            onColumnMoved={uploadColumn}
            onColumnVisible={uploadColumn}
            onColumnPinned={uploadColumn}
            onColumnResized={uploadColumn}
            onGridReady={onGridReady}
            rowClass="cursor-pointer hover:bg-slate-50/50"
            overlayNoRowsTemplate='<span style="padding:10px;color:#9ca3af">No team members found</span>'
          />
        </div>
      </div>

      <EmployeePortalPreview
        open={employeeDetailsDialog.open}
        onClose={closeEmployeePreview}
        iframeRef={employeePreviewFrame}
        accessToken={employeeDetailsDialog.previewAccessToken}
      />

      {/* Manager Detail Dialog */}
      <Dialog
        open={managerDetailDialog.open}
        onClose={() => setManagerDetailDialog({ open: false, type: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          style={{
            margin: 0,
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
            {managerDetailDialog.type === "grants" &&
              `Employee Grants — ${managerDetailDialog.employeeName}`}
            {managerDetailDialog.type === "grant-details" && "Grant Details"}
            {managerDetailDialog.type === "vesting-schedule" &&
              "Vesting Schedule"}
            {managerDetailDialog.type === "approval-action" && "Grant Approval"}
          </span>
          <IconButton
            aria-label="close"
            onClick={() => setManagerDetailDialog({ open: false, type: null })}
            style={{ color: "#9ca3af", padding: "4px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          style={{
            padding: "24px",
            borderTop: "1px solid #f1f5f9",
            borderBottom: "none",
          }}
        >
          {managerDetailDialog.type === "grants" &&
            managerDetailDialog.employeeId && (
              <ManagerGrantsView employeeId={managerDetailDialog.employeeId} />
            )}
          {managerDetailDialog.type === "grant-details" &&
            managerDetailDialog.grantId && (
              <ManagerGrantDetailsView grantId={managerDetailDialog.grantId} />
            )}
          {managerDetailDialog.type === "vesting-schedule" &&
            managerDetailDialog.employeeId && (
              <ManagerVestingView
                employeeId={managerDetailDialog.employeeId}
                grantId={managerDetailDialog.grantId}
                grantIdentifier={managerDetailDialog.grantIdentifier}
              />
            )}
          {managerDetailDialog.type === "approval-action" &&
            managerDetailDialog.grantId && (
              <GrantApprovalActionDialog
                grantId={managerDetailDialog.grantId}
                currentUserId={currentUserId}
                isCurrentUserApprover={
                  managerDetailDialog.isPendingApprover ?? false
                }
                onClose={() =>
                  setManagerDetailDialog({ open: false, type: null })
                }
                onViewGrantHistory={() => {
                  if (!managerDetailDialog.employeeId) return;
                  const employeeId = managerDetailDialog.employeeId;
                  setManagerDetailDialog({ open: false, type: null });
                  gridRef.current?.api?.forEachNode((node) => {
                    const isEmployeeGroup =
                      node.group &&
                      node.allLeafChildren?.some(
                        (child: any) =>
                          child.data?.id === employeeId &&
                          child.data?.grantId != null
                      );
                    if (isEmployeeGroup) node.setExpanded(true);
                  });
                }}
              />
            )}
        </DialogContent>
      </Dialog>
    </>
  );
}
